import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import useManufacturers from "@/hooks/useManufacturers";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Package, Building2, Download, FileText, MessageSquare, Star } from "lucide-react";
import useSmartBack from "@/hooks/useSmartBack";

function EnquiryDialog({ open, onOpenChange, product, companyName }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({}); }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Contact name required"); return; }
    setSaving(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        company_name: form.company_name || null,
        phone: form.phone || null,
        email: form.email || null,
        source: "inbound_enquiry",
        manufacturer_id: product.manufacturer_id || null,
        notes: `Product enquiry: ${product.name}${product.sku ? ` (${product.sku})` : ""}${companyName ? ` — ${companyName}` : ""}${form.notes ? `\n${form.notes}` : ""}`,
      });
      toast.success("Enquiry logged as a new lead");
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Log an enquiry</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Creates a lead for <strong>{product.name}</strong> so the follow-up engine picks it up.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Contact name *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="enquiry-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Company</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="enquiry-company-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold">Phone</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Note</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="enquiry-save-button">{saving ? "Saving…" : "Create lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const goBack = useSmartBack("/products");
  const navigate = useNavigate();
  const mfrs = useManufacturers();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/product-categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground">Product not found.</p>
        <Button variant="outline" className="rounded-2xl mt-4" onClick={() => navigate("/products")}>Back to catalog</Button>
      </div>
    );
  }
  if (!product) return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;

  const company = mfrs.find((m) => m.id === product.manufacturer_id);
  const catById = categories.reduce((a, c) => { a[c.id] = c; return a; }, {});
  const cat = catById[product.category_id];
  const sub = catById[product.subcategory_id];
  const images = product.images || [];
  const specLines = (product.specifications || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto" data-testid="product-detail-page">
      <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 mb-4 tap" data-testid="product-detail-back">
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Gallery */}
        <div>
          <div className="h-72 sm:h-96 rounded-3xl border border-border/60 bg-muted/30 grid place-items-center overflow-hidden">
            {images[imgIdx]
              ? <img src={images[imgIdx]} alt={product.name} className="h-full w-full object-contain" data-testid="product-detail-image" />
              : <Package className="h-16 w-16 text-muted-foreground/40" />}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((src, i) => (
                <button type="button" key={i} onClick={() => setImgIdx(i)} className={`h-16 w-16 rounded-2xl overflow-hidden border-2 ${i === imgIdx ? "border-primary" : "border-border/60"}`} data-testid={`product-thumb-${i}`}>
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {company && (
            <div className="flex items-center gap-2 mb-2">
              <div className="h-9 w-9 rounded-xl border border-border/60 bg-muted/30 grid place-items-center overflow-hidden">
                {company.logo_url ? <img src={company.logo_url} alt={company.name} className="h-full w-full object-contain" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}
              </div>
              <span className="text-sm font-semibold" data-testid="product-detail-company">{company.name}</span>
            </div>
          )}
          <div className="flex items-start gap-2">
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight flex-1" data-testid="product-detail-name">{product.name}</h1>
            {product.featured && <Star className="h-5 w-5 text-amber-500 fill-amber-400 mt-1.5" />}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {product.sku && <Badge variant="outline" className="text-[10px]">Code {product.sku}</Badge>}
            {cat && <Badge variant="outline" className="text-[10px] bg-primary-50 text-primary-700 border-primary-200">{cat.name}</Badge>}
            {sub && <Badge variant="outline" className="text-[10px] bg-primary-50 text-primary-700 border-primary-200">{sub.name}</Badge>}
            {product.is_active === false && <Badge variant="outline" className="text-[10px] bg-stone-100 text-stone-500 border-stone-200">Inactive</Badge>}
          </div>

          {product.unit_price ? (
            <p className="font-display font-black text-2xl text-primary-700 mt-4" data-testid="product-detail-price">
              {fmtINR(product.unit_price)} {product.uom && <span className="text-xs font-semibold text-muted-foreground">/ {product.uom}</span>}
            </p>
          ) : null}

          {(product.short_description || product.description) && (
            <p className="text-sm text-muted-foreground mt-4 whitespace-pre-line">{product.short_description || product.description}</p>
          )}

          <div className="flex flex-wrap gap-2 mt-5">
            <Button className="rounded-2xl" onClick={() => navigate(`/quotations?new=1&product=${product.id}`)} data-testid="product-request-quotation-btn">
              <FileText className="h-4 w-4 mr-1.5" /> Request quotation
            </Button>
            <Button variant="outline" className="rounded-2xl" onClick={() => setEnquiryOpen(true)} data-testid="product-enquiry-btn">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Enquiry
            </Button>
            {product.brochure_url && (
              <a href={`${product.brochure_url}?download=true`} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-2xl" data-testid="product-brochure-btn">
                  <Download className="h-4 w-4 mr-1.5" /> Brochure
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-8">
        {product.short_description && product.description && (
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Description</p>
              <p className="text-sm whitespace-pre-line">{product.description}</p>
            </CardContent>
          </Card>
        )}
        {specLines.length > 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft" data-testid="product-detail-specs">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Specifications</p>
              <ul className="text-sm space-y-1">
                {specLines.map((s, i) => <li key={i} className="flex gap-2"><span className="text-primary-700">•</span><span>{s}</span></li>)}
              </ul>
            </CardContent>
          </Card>
        )}
        {(product.accessories || []).length > 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2">Includes (BOM)</p>
              <div className="flex flex-wrap gap-1.5">
                {product.accessories.map((a, i) => (
                  <Badge key={i} variant="outline" className="text-[11px] bg-emerald-50/60 text-emerald-800 border-emerald-200">✓ {a}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {(product.features || []).length > 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Features</p>
              <ul className="text-sm space-y-1">
                {product.features.map((s, i) => <li key={i} className="flex gap-2"><span className="text-primary-700">•</span><span>{s}</span></li>)}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <EnquiryDialog open={enquiryOpen} onOpenChange={setEnquiryOpen} product={product} companyName={company?.name} />
    </div>
  );
}
